import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Image,
  Layers,
  Music,
  Plus,
  Search,
  Settings2,
  X,
} from 'lucide-react'
import { clockTime, meridiem } from '../api/attendance'
import {
  fetchClasses,
  fetchNewClassOptions,
  group,
  matches,
  needsAttention,
  setClassVisibility,
  shortName,
  NO_FILTER,
  type ClassFilter,
  type ClassRow,
  type GroupBy,
  type Programme,
  type Visibility,
  teachersIn,
} from '../api/classAdmin'
import {
  assignClasses,
  createClassType,
  fetchClassTypes,
  kindNames,
  moved,
  needsAttention as kindGaps,
  stepTarget,
  reorderClassTypes,
  type ClassTypeView,
} from '../api/classTypes'
import { fetchAttendanceGroups, type AttendanceGroup } from '../api/attendanceGroups'
import StudioImage from './StudioImage'
import FilingConflicts from './FilingConflicts'
import ClassKindEditor from './ClassKindEditor'
import CountsAsOneDialog from './CountsAsOneDialog'
import NewClassDialog from './NewClassDialog'

/**
 * Every class the studio runs, and everything about them (D-205).
 *
 * <b>One screen replacing three.</b> Classes, kinds of class and classes-that-
 * count-as-one lived on separate pages in two different menus, split by *who
 * owned the data* rather than *what job somebody was doing* — which was right
 * while every field belonged to Jackrabbit, and became a rewrite the moment
 * fields started coming home.
 *
 * <b>Selection is the one mechanic.</b> "Group these for the website" and
 * "these two count as one" are the same gesture: tick classes, apply a
 * grouping. It carries bulk show and hide as well, so there is one thing to
 * learn rather than three.
 *
 * <b>Group by is the answer to clean and sortable at once.</b> One list, three
 * cuts — Kind for the website work, Day for "what runs Tuesday", Flat for
 * searching — so no screen is the wrong shape for the job in hand. A fourth,
 * Programme, was dropped: it duplicated the Rec/Comp filter beside it (D-223).
 */
export default function ClassesView() {
  const [by, setBy] = useState<GroupBy>('kind')
  const [filter, setFilter] = useState<ClassFilter>(NO_FILTER)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  /*
   * <b>The record has its own address</b> (D-566, completing D-462).
   *
   * D-462 put the open class in `?class=<id>` because a search hit carried one
   * and the list read it from nowhere — the parameter was written by the server
   * and read by nobody. Now that the record is a page rather than a drawer over
   * this list, the id is a path segment and the same problem cannot recur: the
   * URL <i>is</i> the screen.
   *
   * <b>The old parameter still works, and is turned into the new address.</b>
   * A search result is generated live so nothing stale is out there today, but
   * this is a URL people paste to each other, and a silent no-op — the list
   * with nothing open — is exactly the failure D-462 was written about.
   * `replace`, so Back does not bounce off the redirect.
   */
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const legacyClassId = params.get('class')

  useEffect(() => {
    if (legacyClassId) navigate(`/admin/classes/${legacyClassId}`, { replace: true })
  }, [legacyClassId, navigate])

  const [countsAsOne, setCountsAsOne] = useState<AttendanceGroup | null | 'new'>(null)
  const [adding, setAdding] = useState(false)

  const classes = useQuery({ queryKey: ['admin-classes'], queryFn: () => fetchClasses() })
  const kinds = useQuery({ queryKey: ['class-types'], queryFn: fetchClassTypes })
  const groups = useQuery({ queryKey: ['attendance-groups'], queryFn: fetchAttendanceGroups })

  const rows = useMemo(() => classes.data ?? [], [classes.data])

  // Built from the list on screen rather than fetched (D-762).
  const teachers = useMemo(() => teachersIn(rows), [rows])
  const names = useMemo(() => kindNames(kinds.data ?? []), [kinds.data])

  const shown = useMemo(() => rows.filter((r) => matches(r, filter)), [rows, filter])

  // Whether somebody has asked a narrower question than "show me everything".
  // An empty kind is worth a heading in the first case and noise in the second.
  const narrowed =
    filter.search.trim().length > 0 ||
    filter.visibility !== 'all' ||
    filter.programme !== 'all' ||
    // Picking a teacher is a narrower question too (D-762): without this, her
    // classes render under every empty kind in the studio.
    filter.teacher.length > 0 ||
    filter.unconfirmedWeekOnly

  const grouped = useMemo(
    () => group(shown, by, names, kinds.isSuccess, narrowed),
    [shown, by, names, kinds.isSuccess, narrowed],
  )

  /*
   * The kinds actually on screen, in render order (D-218). A filter can hide
   * most of them, and an arrow has to step past what somebody can see.
   */
  const visibleKindIds = useMemo(
    () =>
      by === 'kind'
        ? grouped.map((g) => g.key).filter((k): k is string => k !== null)
        : [],
    [grouped, by],
  )

  const gaps = needsAttention(rows)
  const photoGaps = kindGaps(kinds.data ?? [])

  const chosen = rows.filter((r) => selected.has(r.classId))

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (!next.delete(id)) next.add(id)
      return next
    })

  return (
    <div className="page-shell px-5 py-8 pb-28">
      <header className="mb-5 flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Studio</p>
          <h1 className="font-display text-2xl">All classes</h1>
          <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
            Everything about the classes you run &mdash; how they appear on the website, which
            ones count as one for attendance, and the routine names and music.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-1 flex shrink-0 items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
        >
          <Plus size={14} strokeWidth={2.5} />
          New class
        </button>
      </header>

      <Toolbar by={by} setBy={setBy} filter={filter} setFilter={setFilter} teachers={teachers} />

      {/*
        <b>The same list the import screen shows</b> (D-506), because a class
        filed against its own name does not stop being wrong when the import
        finishes. One component rather than two — the import is when somebody
        is most likely to look, and this is where they are when they wonder
        later. Renders nothing when there is nothing wrong.
      */}
      <div className="mb-4">
        <FilingConflicts />
      </div>

      {/*
        <b>Its own strip, not a fourth clause on the one below</b> (D-603).
        That one is standing studio housekeeping; this is a finite job with a
        list attached — twelve teachers to ask which week their class meets —
        and it disappears the moment the twelfth is answered.
      */}
      {gaps.unconfirmedWeek > 0 && !filter.unconfirmedWeekOnly && (
        <WhichWeek
          count={gaps.unconfirmedWeek}
          onShow={() => setFilter({ ...NO_FILTER, unconfirmedWeekOnly: true })}
        />
      )}

      {(gaps.unfiled > 0 || photoGaps.withoutPhoto > 0 || photoGaps.withoutBlurb > 0) && (
        <Attention
          unfiled={gaps.unfiled}
          withoutPhoto={photoGaps.withoutPhoto}
          withoutBlurb={photoGaps.withoutBlurb}
          onShowUnfiled={() => {
            setBy('kind')
            setFilter({ ...NO_FILTER, visibility: 'public', programme: 'rec' })
          }}
        />
      )}

      {classes.isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {classes.isError && <p role="alert" className="text-sm text-crit">Could not load the classes.</p>}

      {/*
        Said out loud, because the alternative was the screen quietly lying
        (D-209). The classes and the kinds are two requests, and the app restarts
        on every deploy with a cold-start window where the database refuses for
        about a minute. When only the kinds request lost that race the page
        looked completely loaded and captioned every heading "A kind that no
        longer exists" — about data that was entirely fine.
      */}
      {kinds.isError && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-crit bg-crit-bg px-4 py-3 text-[13px]">
          <span className="min-w-0 flex-1 text-ink-2">
            <b className="text-crit">Could not load the kinds of class.</b> Your classes are
            below and nothing is lost &mdash; they just cannot be grouped until this loads.
          </span>

          <button
            type="button"
            onClick={() => void kinds.refetch()}
            disabled={kinds.isFetching}
            className="shrink-0 rounded-lg border border-crit px-3 py-1.5 text-[12px] font-semibold text-crit disabled:opacity-40"
          >
            {kinds.isFetching ? 'Trying...' : 'Try again'}
          </button>
        </div>
      )}

      {classes.data && shown.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[13.5px] text-ink-3">
          Nothing matches that.
        </p>
      )}

      {/*
        Said, because nothing on screen explained why anybody would reorder
        these (D-214). The arrows are meaningless until you know the order is
        the one families read.
      */}
      {by === 'kind' && kinds.isSuccess && grouped.length > 1 && (
        <p className="mb-3 text-[12.5px] text-ink-3">
          This order is the order families see on the website. Use the arrows to move a kind up
          or down.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {grouped.map((g) => (
          <GroupSection
            key={g.key ?? 'none'}
            by={by}
            heading={g.label}
            rows={g.rows}
            kind={g.key !== null && by === 'kind' ? findKind(kinds.data, g.key) : null}
            kindOrder={(kinds.data ?? []).map((k) => k.classTypeId)}
            visibleKindIds={visibleKindIds}
            searching={filter.search.trim().length > 0}
            groups={groups.data ?? []}
            selected={selected}
            onToggle={toggle}
            onEditGroup={setCountsAsOne}
          />
        ))}
      </div>

      {by === 'kind' && kinds.data && <NewKind />}

      {chosen.length > 0 && (
        <SelectionBar
          chosen={chosen}
          kinds={kinds.data ?? []}
          groups={groups.data ?? []}
          onClear={() => setSelected(new Set())}
          onCountAsOne={setCountsAsOne}
        />
      )}

      {adding && (
        <NewClassDialog
          kinds={kinds.data ?? []}
          onCreated={() => {
            setAdding(false)
            // Cleared, so a new class is not hiding behind "Showing: on the
            // site" — it starts hidden, which is the one filter that would
            // make it invisible the moment it was made.
            setFilter(NO_FILTER)
          }}
          onClose={() => setAdding(false)}
        />
      )}

      {countsAsOne !== null && (
        <CountsAsOneDialog
          selection={countsAsOne === 'new' ? chosen : membersOf(countsAsOne, rows)}
          existing={countsAsOne === 'new' ? null : countsAsOne}
          onClose={() => {
            setCountsAsOne(null)
            setSelected(new Set())
          }}
        />
      )}
    </div>
  )
}

const findKind = (kinds: ClassTypeView[] | undefined, id: string) =>
  kinds?.find((k) => k.classTypeId === id) ?? null

const membersOf = (group: AttendanceGroup, rows: ClassRow[]) =>
  group.alternatives
    .map((a) => rows.find((r) => r.classId === a.studioClassId))
    .filter((r): r is ClassRow => r !== undefined)

// ---- The toolbar ----

function Toolbar({
  by,
  setBy,
  filter,
  setFilter,
  teachers,
}: {
  by: GroupBy
  setBy: (by: GroupBy) => void
  filter: ClassFilter
  setFilter: (filter: ClassFilter) => void
  teachers: string[]
}) {
  return (
    <div className="mb-4 flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex min-w-[14rem] flex-1 items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 focus-within:border-accent">
          <Search size={16} strokeWidth={2} className="shrink-0 text-ink-3" />
          <input
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Search a class, routine, room or day"
            aria-label="Search classes"
            className="min-w-0 flex-1 bg-transparent text-[13.5px] outline-none"
          />
          {filter.search && (
            <button
              type="button"
              onClick={() => setFilter({ ...filter, search: '' })}
              aria-label="Clear the search"
              className="shrink-0 text-ink-3 hover:text-ink"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>

        <Segment
          label="Group by"
          value={by}
          onChange={setBy}
          options={[
            ['kind', 'Kind'],
            ['day', 'Day'],
            ['flat', 'Flat'],
          ]}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Segment
          label="Showing"
          value={filter.visibility}
          onChange={(visibility: Visibility) => setFilter({ ...filter, visibility })}
          options={[
            ['all', 'All'],
            ['public', 'On the site'],
            ['hidden', 'Hidden'],
          ]}
        />

        <Segment
          value={filter.programme}
          onChange={(programme: Programme) => setFilter({ ...filter, programme })}
          options={[
            ['all', 'Rec & comp'],
            ['rec', 'Rec'],
            ['comp', 'Comp'],
          ]}
        />

        {/*
          <b>Picked, not typed</b> (D-762). `asked`: *"add a filter on this
          page to search by teacher, so we don't have to type there names in."*

          <b>A select rather than a fourth segment</b>, because there are
          twenty-odd teachers and a segmented control is for three or four
          choices. It is also why this is a native control: a list this long
          wants the platform's own picker on a phone.

          <b>Only when there is somebody to pick</b> — a filter offering
          nothing is a control that cannot do anything, which is what D-204
          objects to.
        */}
        {teachers.length > 0 && (
          <label className="flex items-center gap-2 text-[12.5px] text-ink-3">
            Teacher
            <select
              value={filter.teacher}
              onChange={(e) => setFilter({ ...filter, teacher: e.target.value })}
              className="min-h-[1.75rem] rounded-lg border border-line-2 bg-surface px-2 py-1 text-[12.5px] font-semibold text-ink-2"
            >
              <option value="">Anyone</option>
              {teachers.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}

        {/*
          Not a fourth segment (D-603). It is a job that ends, so it appears
          only while it is on — but it has to appear, or the list is short for
          a reason nothing on the screen gives.
        */}
        {filter.unconfirmedWeekOnly && (
          <button
            type="button"
            onClick={() => setFilter({ ...filter, unconfirmedWeekOnly: false })}
            className="flex min-h-[1.75rem] items-center gap-1.5 rounded-lg border border-accent px-2.5 py-1.5 text-[12.5px] font-semibold text-accent-2"
          >
            Waiting on a week
            <X size={13} strokeWidth={2.5} aria-label="Show every class again" />
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * The twelve classes the import believes alternate, with nobody having said
 * which fortnight (D-603).
 *
 * <b>A list, not a warning.</b> Jackrabbit has no field for a cadence, so the
 * word `BIWKLY` in a class name is the only signal there is — and it cannot
 * say which week. Every one of these is a teacher somebody has to ask, which
 * is a finite job with an end, so the strip goes away by itself.
 */
function WhichWeek({ count, onShow }: { count: number; onShow: () => void }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border border-accent bg-surface-2 px-4 py-3 text-[12.5px]">
      <span className="font-semibold text-accent-2">Which week?</span>

      <span className="min-w-0 flex-1 text-ink-2">
        {count} {count === 1 ? 'class meets' : 'classes meet'} every other week, going by the
        name. The calendar has picked a fortnight and nobody has confirmed it, so{' '}
        {count === 1 ? 'it may be' : 'they may be'} a week out. Ask the teacher, then set the
        first evening.
      </span>

      <button
        type="button"
        onClick={onShow}
        className="shrink-0 rounded-lg border border-accent px-2.5 py-1.5 text-[12px] font-semibold text-accent-2"
      >
        Show me
      </button>
    </div>
  )
}

/** Pick one of a few. A trough with the chosen one filled, so it never reads as a set of independent toggles (D-768). */
function Segment<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label?: string
  value: T
  onChange: (value: T) => void
  options: [T, string][]
}) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-[11.5px] font-semibold text-ink-3">{label}</span>}
      <div className="flex rounded-lg border border-line-2 bg-surface p-0.5">
        {options.map(([key, text]) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={value === key}
            className={`rounded-[0.35rem] px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${
              value === key ? 'bg-accent text-on-accent' : 'text-ink-3 hover:text-ink'
            }`}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * What is unfinished, said rather than left to be found.
 *
 * Every one of these is invisible until somebody opens the public site and
 * scrolls, which is not a check anybody performs on a Tuesday evening.
 */
function Attention({
  unfiled,
  withoutPhoto,
  withoutBlurb,
  onShowUnfiled,
}: {
  unfiled: number
  withoutPhoto: number
  withoutBlurb: number
  onShowUnfiled: () => void
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border border-warn bg-warn-bg px-4 py-3 text-[12.5px]">
      <span className="font-semibold text-warn">Not finished yet</span>

      <span className="min-w-0 flex-1 text-ink-2">
        {unfiled > 0 && (
          <>
            {unfiled} public {unfiled === 1 ? 'class is' : 'classes are'} in no kind, so{' '}
            {unfiled === 1 ? 'it appears' : 'they appear'} under &ldquo;More classes&rdquo;
          </>
        )}
        {unfiled > 0 && (withoutPhoto > 0 || withoutBlurb > 0) && <> &middot; </>}
        {withoutPhoto > 0 && (
          <>
            {withoutPhoto} {withoutPhoto === 1 ? 'kind has' : 'kinds have'} no photograph
          </>
        )}
        {withoutPhoto > 0 && withoutBlurb > 0 && <> &middot; </>}
        {withoutBlurb > 0 && (
          <>
            {withoutBlurb} {withoutBlurb === 1 ? 'has' : 'have'} no description
          </>
        )}
      </span>

      {unfiled > 0 && (
        <button
          type="button"
          onClick={onShowUnfiled}
          className="shrink-0 rounded-lg border border-warn px-2.5 py-1 text-[11.5px] font-semibold text-warn"
        >
          Show me
        </button>
      )}
    </div>
  )
}

// ---- One group, with its heading ----

function GroupSection({
  by,
  heading,
  rows,
  kind,
  kindOrder,
  visibleKindIds,
  searching,
  groups,
  selected,
  onToggle,
  onEditGroup,
}: {
  by: GroupBy
  heading: string
  rows: ClassRow[]
  kind: ClassTypeView | null
  /** Every kind id in the order they appear, so a drop lands where it was aimed. */
  kindOrder: string[]
  /** Only the kinds on screen, so an arrow steps past one somebody can see. */
  visibleKindIds: string[]
  /** A search is running, so the groups open rather than hiding the matches. */
  searching: boolean
  groups: AttendanceGroup[]
  selected: Set<string>
  onToggle: (id: string) => void
  onEditGroup: (group: AttendanceGroup) => void
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)

  /*
   * Closed to begin with, so the screen opens as a list of kinds rather than
   * 185 rows (D-214).
   *
   * <b>Except while searching.</b> Collapsed-by-default plus a search box
   * returns a page of closed boxes with the matches hidden inside them — the
   * tidier page would have broken the most-used control on it. So a search
   * opens every group that still has rows, and closing one by hand still wins
   * while the search stands.
   */
  const [toggled, setToggled] = useState<boolean | null>(null)
  const collapsed = toggled ?? !searching

  /*
   * Reordering, and saying so when it does not work (D-218).
   *
   * This had `onSuccess` and nothing else, so a refused or failed request left
   * the arrows looking like dead buttons — which is indistinguishable from a
   * feature that does nothing, and is exactly how it was reported. A control
   * that fails silently teaches people it is broken even on the day it works.
   *
   * Refetching rather than trusting the response is the other half: whatever
   * the server now believes is the truth, and the screen should show that
   * rather than what it hoped for.
   */
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderClassTypes(ids),
    onSuccess: (types) => queryClient.setQueryData(['class-types'], types),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['class-types'] }),
  })

  const publicClasses = rows.filter((r) => r.showInPublicCatalog).length

  /*
   * Where this kind sits in the real list, not where it sits on screen.
   *
   * Those differ the moment a filter hides one — dropping onto the third
   * visible heading would otherwise move a kind to position three of the
   * studio's actual order, which is a different place entirely.
   */
  const position = kind ? kindOrder.indexOf(kind.classTypeId) : -1

  const upTo = kind ? stepTarget(kindOrder, visibleKindIds, kind.classTypeId, 'up') : null
  const downTo = kind ? stepTarget(kindOrder, visibleKindIds, kind.classTypeId, 'down') : null

  if (by === 'flat') {
    return (
      <div className="overflow-hidden rounded-2xl border border-line bg-surface">
        <ClassList
          rows={rows}
          heading=""
          groups={groups}
          selected={selected}
          onToggle={onToggle}
          onEditGroup={onEditGroup}
        />
      </div>
    )
  }

  return (
    <section
      className="overflow-hidden rounded-2xl border border-line bg-surface"
      /*
       * Dragging still works, as a nicety for whoever reaches for it (D-214).
       *
       * `text/plain`, not a custom type: Safari routinely refuses to read a
       * non-standard drag format back, so the drop found an empty string,
       * `indexOf` returned -1, and the guard below skipped the reorder in
       * silence. On a Mac — which is where this is used — the handle simply did
       * nothing, with no way to tell that from "nothing to do".
       */
      onDragOver={kind ? (e) => e.preventDefault() : undefined}
      onDrop={
        kind
          ? (e) => {
              e.preventDefault()
              const from = kindOrder.indexOf(e.dataTransfer.getData('text/plain'))
              if (from >= 0 && from !== position) {
                reorder.mutate(moved(kindOrder, from, position))
              }
            }
          : undefined
      }
    >
      {/*
        Said out loud rather than swallowed (D-218). Whatever stops a reorder —
        a refusal, a cold database, a stale tab — the person who pressed the
        arrow finds out, instead of concluding the button does nothing.
      */}
      {reorder.isError && (
        <p role="alert" className="border-b border-crit bg-crit-bg px-4 py-2 text-[12.5px] text-crit">
          Could not change the order: {(reorder.error as Error).message}
        </p>
      )}

      {/*
        The whole heading is the handle (D-218).

        A grip is a target you have to find and hit; the row is the thing being
        moved, so it is the thing to grab. `onDragStart` stays on the header
        rather than the section, or dropping a row onto itself starts and ends
        the same gesture.
      */}
      <header
        draggable={kind !== null}
        onDragStart={
          kind ? (e) => e.dataTransfer.setData('text/plain', kind.classTypeId) : undefined
        }
        className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
          kind ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
      >
        {/*
          The arrows stay, because dragging is a gesture people have to guess at
          and these say what they do — and they work from a keyboard, which drag
          never will.
        */}
        {kind && (
          <span className="-ml-1 flex shrink-0 flex-col">
            <button
              type="button"
              draggable={false}
              disabled={upTo === null || reorder.isPending}
              onClick={() => upTo !== null && reorder.mutate(moved(kindOrder, position, upTo))}
              aria-label={`Move ${kind.name} up`}
              className="rounded p-0.5 text-ink-3 hover:text-ink disabled:opacity-25"
            >
              <ChevronUp size={14} strokeWidth={2.5} />
            </button>

            <button
              type="button"
              draggable={false}
              disabled={downTo === null || reorder.isPending}
              onClick={() => downTo !== null && reorder.mutate(moved(kindOrder, position, downTo))}
              aria-label={`Move ${kind.name} down`}
              className="rounded p-0.5 text-ink-3 hover:text-ink disabled:opacity-25"
            >
              <ChevronDown size={14} strokeWidth={2.5} />
            </button>
          </span>
        )}

        {kind &&
          (kind.heroUrl ? (
            <StudioImage
              src={kind.heroUrl}
              alt=""
              framing={kind.heroFraming}
              className="size-11 shrink-0 rounded-lg"
            />
          ) : (
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-surface-2 text-ink-3">
              <Image size={15} strokeWidth={2} />
            </span>
          ))}

        <button
          type="button"
          onClick={() => setToggled(!collapsed)}
          aria-expanded={!collapsed}
          className="min-w-0 flex-1 text-left"
        >
          <span className="block text-[14.5px] font-semibold wrap-anywhere">{heading}</span>
          <span className="mt-0.5 block text-[12px] text-ink-3">
            {rows.length} {rows.length === 1 ? 'class' : 'classes'}
            {/*
              A shut gate is said here rather than only inside the editor
              (D-261). "6 on the site" over a kind that is switched off is the
              screen stating the opposite of what the public page shows, and
              nobody opens an editor to check a heading they believe.
            */}
            {kind && !kind.showInPublicCatalog ? (
              <> &middot; off the website</>
            ) : (
              kind &&
              publicClasses < rows.length && <> &middot; {publicClasses} on the site</>
            )}
            {kind && !kind.heroUrl && <> &middot; no photograph</>}
          </span>
        </button>

        {kind && (
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            aria-expanded={editing}
            aria-label={`Photo and description for ${kind.name}`}
            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold ${
              editing
                ? 'border-accent text-accent'
                : 'border-line-2 text-ink-2 hover:border-ink-3'
            }`}
          >
            <Settings2 size={13} strokeWidth={2.5} />
            {editing ? 'Done' : 'Photo & text'}
          </button>
        )}
      </header>

      {kind && editing && (
        <ClassKindEditor
          kind={kind}
          publicClasses={publicClasses}
          totalClasses={rows.length}
          onClose={() => setEditing(false)}
        />
      )}

      {!collapsed && (
        <ClassList
          rows={rows}
          /*
           * Rows lead with what distinguishes them: under TIPPY TOES, repeating
           * the prefix seven times is noise. Display only — the stored name is
           * untouched, because D-88 keys class identity on it.
           */
          heading={by === 'kind' ? heading : ''}
          groups={groups}
          selected={selected}
          onToggle={onToggle}
          onEditGroup={onEditGroup}
        />
      )}
    </section>
  )
}

function ClassList({
  rows,
  heading,
  groups,
  selected,
  onToggle,
  onEditGroup,
}: {
  rows: ClassRow[]
  heading: string
  groups: AttendanceGroup[]
  selected: Set<string>
  onToggle: (id: string) => void
  onEditGroup: (group: AttendanceGroup) => void
}) {
  if (rows.length === 0) {
    // A kind made a moment ago, or one every class of which the filter hid.
    // Said, because an empty box under a heading reads as something broken.
    return (
      <p className="border-t border-line px-4 py-3 text-[12.5px] text-ink-3">
        Nothing is in this yet, so it does not appear on the public page. Tick classes below and
        put them in it.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-line border-t border-line">
      {rows.map((row) => (
        <li key={row.classId} className="flex items-center gap-3 px-4 py-2.5">
          <input
            type="checkbox"
            checked={selected.has(row.classId)}
            onChange={() => onToggle(row.classId)}
            aria-label={`Select ${row.name}`}
            className="size-4 shrink-0 accent-[var(--color-accent)]"
          />

          <span className="w-[5.5rem] shrink-0 text-[12px] text-ink-3 tabular">
            {row.dayOfWeek.slice(0, 3)}
            {row.startTime ? (
              <>
                {' '}
                {clockTime(row.startTime)}
                {meridiem(row.startTime)}
              </>
            ) : (
              <span className="ml-1 text-warn">no time</span>
            )}
          </span>

          {/*
            <b>A link, now that the record has an address</b> (D-566). It was a
            button calling `navigate`, which is the right shape for opening a
            drawer and the wrong one for going somewhere: middle-click,
            &#8984;-click and <i>copy link address</i> all do nothing on it, and
            a screen reader announces a button rather than a destination. The
            three ways into `/admin/dancers/:id` are all links, and this is the
            same kind of row.

            The class name is the link, not the whole row: the row also carries
            a visibility toggle and a tick box, and a link wrapping those would
            swallow them.
          */}
          <Link to={`/admin/classes/${row.classId}`} className="min-w-0 flex-1 text-left">
            <span className="block text-[13px] wrap-anywhere">
              {row.routineName ?? shortName(row.name, heading)}
            </span>
            {row.routineName && (
              <span className="block text-[11.5px] text-ink-3 wrap-anywhere">{row.name}</span>
            )}
          </Link>

          <span className="flex shrink-0 items-center gap-1.5">
            {row.hasMusic && (
              <span title="Music folder on file" className="text-good">
                <Music size={12} strokeWidth={2.5} />
              </span>
            )}

            {/*
              The rule's own name, not just its arithmetic (D-226). "1 of 2"
              alone says nothing about *what* is one of two, and gives no way to
              tell two rules apart on a screen holding several.
            */}
            {groupsFor(row, groups).map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => onEditGroup(group)}
                title={`Attendance: ${group.name}`}
                className="flex min-w-0 items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-2 hover:text-ink"
              >
                <Layers size={10} strokeWidth={2.5} className="shrink-0" />
                <span className="truncate">{group.name}</span>
                <span className="tabular shrink-0 text-ink-3">
                  {group.requiredPerWeek} of {group.alternatives.length}
                </span>
              </button>
            ))}

            {row.ageLabel && (
              <span className="hidden text-[11.5px] text-ink-3 sm:inline">{row.ageLabel}</span>
            )}

            {/*
              A dot rather than a word. Every row carries it, so a label would
              be the widest column on the screen saying the same thing 185
              times — and hidden is the state worth noticing, not public.
            */}
            <span
              title={row.showInPublicCatalog ? 'On the website' : 'Not on the website'}
              className={`size-2 rounded-full ${
                row.showInPublicCatalog ? 'bg-accent' : 'bg-line-2'
              }`}
            />

            <ChevronRight size={14} strokeWidth={2.5} className="text-ink-3" />
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Every set this class is in, in the order the list holds them.
 *
 * Plural since D-657: the studio's combo serves three levels at once, and a
 * badge showing one of them gives no hint the others exist — which is how
 * somebody edits a rule believing it is the only one on the class.
 */
const groupsFor = (row: ClassRow, groups: AttendanceGroup[]) =>
  groups.filter((g) => row.attendanceGroupIds.includes(g.id))

// ---- What a selection can do ----

/**
 * The bar that appears once anything is ticked.
 *
 * Every bulk action lives here, so "file these under a kind", "these count as
 * one" and "take these off the site" are one gesture with three endings rather
 * than three workflows in three places.
 */
function SelectionBar({
  chosen,
  kinds,
  groups,
  onClear,
  onCountAsOne,
}: {
  chosen: ClassRow[]
  kinds: ClassTypeView[]
  groups: AttendanceGroup[]
  onClear: () => void
  onCountAsOne: (group: AttendanceGroup | 'new') => void
}) {
  const queryClient = useQueryClient()

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-classes'] })
    queryClient.invalidateQueries({ queryKey: ['class-types'] })

    /*
     * <b>And every open class, because this changed one</b> (D-496).
     *
     * D-439 fixed the panel's own picker and said in as many words that this
     * bar was the same shape and deliberately left: it refreshed the list and
     * not the class, so filing a class here and reopening its panel within
     * the 30-second `staleTime` showed the kind it used to have. Identical to
     * the bug that was reported twice — a save that worked, displayed as one
     * that did not.
     *
     * The key is the prefix, not one id: this bar files a whole selection.
     */
    queryClient.invalidateQueries({ queryKey: ['admin-class'] })
  }

  const ids = chosen.map((c) => c.classId)

  const file = useMutation({
    mutationFn: (kindId: string | null) => assignClasses(ids, kindId),
    onSuccess: () => {
      refresh()
      onClear()
    },
  })

  const visibility = useMutation({
    mutationFn: (visible: boolean) => setClassVisibility(ids, visible),
    onSuccess: () => {
      refresh()
      onClear()
    },
  })

  // Offered as an edit when the whole selection is already one set, so that
  // "these count as one" never silently rebuilds a rule somebody has tuned.
  const shared = sharedGroup(chosen, groups)

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4">
      <div className="flex max-w-full flex-wrap items-center gap-2 rounded-2xl border border-line bg-surface px-3.5 py-2.5 shadow-2xl">
        <span className="text-[12.5px] font-semibold tabular">{chosen.length} selected</span>

        <select
          value=""
          onChange={(e) => file.mutate(e.target.value === 'none' ? null : e.target.value)}
          aria-label="Put in a kind"
          className="rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-accent"
        >
          <option value="" disabled>
            Put in a kind...
          </option>
          {kinds.map((k) => (
            <option key={k.classTypeId} value={k.classTypeId}>
              {k.name}
            </option>
          ))}
          <option value="none">Not in a kind</option>
        </select>

        {/*
          Names the rule as the dancer meets it, and only when it can be made
          (D-226).

          "Count as one" said neither. One *what* was never stated — the answer
          is one attendance — and it hardcoded a number the dialog lets you
          change, so a rule tuned to 2 of 3 was still offered by a button
          claiming one. It was also the only control here that opens a dialog
          and the only one without an ellipsis, so it read as something that
          would fire straight at the selection.

          Dead below two, because one class can only make "attend 1 of 1",
          which the dialog itself calls pointless. Saying why beats a button
          that opens onto a warning (D-204).
        */}
        <button
          type="button"
          onClick={() => onCountAsOne(shared ?? 'new')}
          disabled={chosen.length < 2}
          title={chosen.length < 2 ? 'Pick two or more classes' : undefined}
          className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40 disabled:hover:border-line-2"
        >
          {shared ? 'Edit what is required...' : 'Attend one of these...'}
        </button>

        <button
          type="button"
          onClick={() => visibility.mutate(true)}
          className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3"
        >
          Show
        </button>

        <button
          type="button"
          onClick={() => visibility.mutate(false)}
          className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3"
        >
          Hide
        </button>

        <button
          type="button"
          onClick={onClear}
          aria-label="Clear the selection"
          className="rounded-lg p-1.5 text-ink-3 hover:text-ink"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

/**
 * The set every chosen class already belongs to, when they share exactly one.
 *
 * <b>An intersection since D-657</b>, because a class may be in several. Two
 * classes sharing one set offer *Edit what is required*; sharing none, or
 * sharing two, offers *Attend one of these* and builds a new one — editing
 * would have to ask which, and the dialog has no way to.
 */
export function sharedGroup(
  chosen: ClassRow[],
  groups: AttendanceGroup[],
): AttendanceGroup | null {
  if (chosen.length === 0) return null

  const shared = chosen
    .map((c) => c.attendanceGroupIds)
    .reduce((all, ids) => all.filter((id) => ids.includes(id)))

  if (shared.length !== 1) return null

  return groups.find((g) => g.id === shared[0]) ?? null
}

/** Adding a kind by hand — a workshop, a summer intensive, anything new. */
function NewKind() {
  const queryClient = useQueryClient()
  const [name, setName] = useState<string | null>(null)
  const [programId, setProgramId] = useState('')

  /*
   * The programmes, from the options the new-class form already loads (D-227).
   *
   * Asked here rather than left blank because a kind derives its programme from
   * the classes filed under it, and a kind created by hand has none yet — so an
   * unclassified one sits outside the Rec/Comp filter it was made to appear in,
   * and looks like it failed to save.
   */
  const { data: options } = useQuery({
    queryKey: ['new-class-options'],
    queryFn: fetchNewClassOptions,
    staleTime: 300_000,
  })

  const create = useMutation({
    mutationFn: () =>
      createClassType({
        name: name ?? '',
        blurb: null,
        heroMediaAssetId: null,
        sortOrder: 0,
        studioProgramId: programId || null,
      }),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)
      setName(null)
      setProgramId('')
    },
  })

  if (name === null) {
    return (
      <button
        type="button"
        onClick={() => setName('')}
        className="mt-3 flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-line-2 px-4 py-3 text-[13px] font-semibold text-ink-2 hover:border-accent hover:text-accent"
      >
        Add a kind of class
      </button>
    )
  }

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 rounded-2xl border border-line bg-surface px-4 py-3.5">
      <label className="flex min-w-[14rem] flex-1 flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-3">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          placeholder="Adult Tap"
          className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent"
        />
      </label>

      {/*
        What it is, alongside what it is called (D-227).

        "Not said yet" stays a choice rather than being forced — a kind whose
        programme nobody has decided is a real state, and the next import fills
        it in from whatever gets filed here. It is only ever a label: the 80%
        rule and capacity read the class's own programme, never this.
      */}
      <label className="flex min-w-[10rem] flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-3">What kind</span>
        <select
          value={programId}
          onChange={(e) => setProgramId(e.target.value)}
          className="w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent"
        >
          <option value="">Not said yet</option>
          {(options?.programs ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        disabled={name.trim().length === 0 || create.isPending}
        onClick={() => create.mutate()}
        className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
      >
        {create.isPending ? 'Adding...' : 'Add'}
      </button>

      <button
        type="button"
        onClick={() => setName(null)}
        className="py-2 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
      >
        Cancel
      </button>
    </div>
  )
}
